import { ENTITIES, FEATURES, PROVIDERS } from "constants/queryKeys"

import * as R from "ramda"
import { useQuery } from "react-query"

import { IBackendDealInfo } from "./types"

export interface IBackendDealResponse {
  live: IBackendDealInfo[]
  funded: IBackendDealInfo[]
  upcoming: IBackendDealInfo[]
}

const dealKeys = {
  all: [{ feature: FEATURES.DEAL_BACKEND, provider: PROVIDERS.BACKEND }] as const,
  list: {
    all: [
      { feature: FEATURES.DEAL_BACKEND, provider: PROVIDERS.BACKEND, entity: ENTITIES.LIST },
    ] as const,
  },
}

const sortByStartTime = (a: IBackendDealInfo, b: IBackendDealInfo) => {
  if (!a.desiredStartTime) {
    return 1
  }
  if (!b.desiredStartTime) {
    return -1
  }
  const aStart = new Date(a.desiredStartTime)
  const bStart = new Date(b.desiredStartTime)
  return aStart.getTime() - bStart.getTime()
}

const sortByStartTimeReversed = (a: IBackendDealInfo, b: IBackendDealInfo) => {
  if (!a.desiredStartTime) {
    return -1
  }
  if (!b.desiredStartTime) {
    return 1
  }
  const aStart = new Date(a.desiredStartTime)
  const bStart = new Date(b.desiredStartTime)
  return bStart.getTime() - aStart.getTime()
}

const toBigNumber = (str: string) => str.toBigNumber(0)
export const addDealBigNumbers = (deal: IBackendDealInfo) =>
  R.evolve({
    dealSize: toBigNumber,
    totalRaised: toBigNumber,
    minAllocation: toBigNumber,
  })(deal as any)

const allDealsMap = (arr: IBackendDealInfo[]) => arr.map(addDealBigNumbers)
const allDealsMapWithSort = (arr: IBackendDealInfo[]) =>
  arr.map(addDealBigNumbers).sort(sortByStartTime as any)
const allDealsMapWithSortReversed = (arr: IBackendDealInfo[]) =>
  arr.map(addDealBigNumbers).sort(sortByStartTimeReversed as any)

// Snapshot of the backend's /deal/all response, taken before the backend was
// shut down. Loaded with a dynamic import so it is split into its own chunk.
const getDeals = async (): Promise<IBackendDealResponse> => {
  const { default: data } = await import("./deals.snapshot.json")
  return R.evolve({
    live: allDealsMapWithSortReversed,
    funded: allDealsMap,
    upcoming: allDealsMapWithSort,
  })(data as any) as IBackendDealResponse
}

// A 'deal' is a single deal from the snapshot. These are ALL the deals in the
// system (upcoming, live and funded)
export const useOfferings = () => {
  return useQuery<IBackendDealResponse, Error>(dealKeys.list.all, getDeals, {
    staleTime: Infinity,
  })
}
